import { createElement, HTMLAttributes, PropsWithChildren } from 'react';
import { CircularProgress } from '@mui/material';
import styles from './PureTable.module.scss';
import { classes } from '../../../lib/utils/cssUtils';
import { BinIcon } from '../../../lib/icons/BinIcon';
import {
  BasePureTableActionProps,
  ExtendedActionProps,
  ExtraPureTableActionProps,
  PureTableAction,
  ResponseActionProps,
  VerifiedExtendedActionProps,
} from './PureTable.types';
import { Label } from '../Label/Label';
import { useAttributeLabel } from '../../../lib/hooks/labels';
import { ATTRIBUTES } from '../../../lib/constants/attributes';
import { TABLE_LABELS } from '../../../lib/constants/labelKeys';
import { DownloadIcon } from '../../../lib/icons/DowloadIcon';
import Badge from '../Badge/Badge';
import { tableIds } from '../../../lib/tests/testIds';
import { MagnifyingGlassIcon } from '../../../lib/icons/MagnifyingGlass';
import { PlusIcon } from '../../../lib/icons/PlusIcon';

const getAttributeLabel = (action: PureTableAction) =>
  useAttributeLabel(ATTRIBUTES.ARIA_LABEL, TABLE_LABELS.ACTION(action));

const getTitleLabel = (action: PureTableAction) => useAttributeLabel(ATTRIBUTES.TITLE, TABLE_LABELS.ACTION(action));

const BaseAction = ({ children, className, withoutActionMarker, asDiv, ...rest }: BasePureTableActionProps) =>
  createElement(
    asDiv ? 'div' : 'button',
    {
      className: classes(styles.actionButtonStyling, !withoutActionMarker && styles.actionButtonMarker, className),
      ...rest,
    },
    children,
  );

const ExtendedAction = ({ titleKey, withLabel, icon, ...props }: VerifiedExtendedActionProps) => (
  <BaseAction {...(!withLabel && { ...getAttributeLabel(titleKey), ...getTitleLabel(titleKey) })} {...props}>
    {icon}
    {withLabel && <Label name={TABLE_LABELS.ACTION(titleKey)} />}
  </BaseAction>
);

export const DeleteAction = ({ className, onClick, id, ...props }: ExtendedActionProps) => (
  <ExtendedAction
    onClick={() => onClick?.(id)}
    icon={<BinIcon />}
    className={classes(styles.onDelete, className)}
    {...props}
  />
);

export const AddAction = ({ className, onClick, id, ...props }: ExtendedActionProps) => (
  <ExtendedAction
    onClick={() => onClick?.(id)}
    icon={<PlusIcon />}
    className={classes(styles.onDelete, className)}
    {...props}
  />
);
export const OpenDetails = ({ className, onClick, ...props }: ExtendedActionProps) => (
  <ExtendedAction
    onClick={id => onClick?.(id)}
    icon={
      <div className={styles.magnifyingIconContainer}>
        <MagnifyingGlassIcon />
      </div>
    }
    className={classes(styles.onIndividuals, className)}
    {...props}
  />
);

export const DownloadAction = ({ className, onClick, ...props }: ExtendedActionProps) => (
  <ExtendedAction
    onClick={id => onClick?.(id)}
    icon={
      <div className={styles.actionIconContainer}>
        <DownloadIcon />
      </div>
    }
    className={classes(styles.onDownload, className)}
    {...props}
  />
);

export const ResponseActions = ({ onApprove, onReject, id }: ResponseActionProps) => (
  <>
    <BaseAction onClick={() => onApprove(id)} className={styles.onApprove}>
      <Label name={TABLE_LABELS.ACTION('approve')} />
    </BaseAction>
    <BaseAction onClick={() => onReject(id)} className={styles.onReject}>
      <Label name={TABLE_LABELS.ACTION('reject')} />
    </BaseAction>
  </>
);

export const RemindAction = ({ onClick, id }: BasePureTableActionProps & ExtraPureTableActionProps) => (
  <BaseAction onClick={() => onClick?.(id)} className={styles.onRemind}>
    <Label name={TABLE_LABELS.ACTION('remind')} />
  </BaseAction>
);

export const TableBadge = ({ children }: PropsWithChildren) => (
  <Badge variant="warn" className={styles.overdue}>
    {children}
  </Badge>
);

export const Loader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={classes(styles.loaderContainer, styles.actionButtonMarker, className)}
    {...props}
    data-testid={tableIds.loader}
  >
    <div className={styles.loader}>
      <CircularProgress size="100%" />
    </div>
  </div>
);
